import { Select, Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { getAccessibleDepartmentOptions } from '@/module/Department/model/DepartmentQuery';
import { EvaluationDepartmentSummary } from '@/module/Evaluation/EvaluationDepartmentSummary';
import { EvaluationHeader } from '@/module/Evaluation/components/EvaluationHeader';
import { getEvaluationOptions } from '@/module/Evaluation/model/EvaluationQuery';

import { Content, NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';

const evaluationSummarySchema = z.object({
  departmentId: z.coerce.string().nullish(),
});

export const Route = createFileRoute('/_auth/evaluation/_layout/$evaluationId/department-summary-results')({
  component: RouteComponent,
  validateSearch: evaluationSummarySchema,
  loader: async ({ params: { evaluationId }, context: { queryClient } }) => {
    const evaluation = await queryClient.ensureQueryData(getEvaluationOptions(evaluationId));

    return {
      evaluation,
      crumb: evaluation.title,
    };
  },
});

function RouteComponent() {
  const { evaluationId } = Route.useParams();
  const { departmentId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: evaluation } = useSuspenseQuery(getEvaluationOptions(evaluationId));
  const { data: departments } = useSuspenseQuery(getAccessibleDepartmentOptions());

  const { currentRole } = useCurrentUser();
  const hasAccess = ['DEPARTMENT_ADMIN', 'FACULTY_ADMIN', 'UNIVERSITY_ADMIN'].indexOf(currentRole) !== -1;

  const onDepartmentChange = (newValue: string | null) => {
    navigate({
      to: '.',
      search: prev => ({ ...prev, departmentId: newValue }),
    });
  };

  if (!hasAccess) {
    return (
      <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de director departament sau decan" />
    );
  }

  return (
    <Stack>
      <EvaluationHeader
        hideSummary
        evaluation={evaluation}
      />
      <Content type="full">
        <Stack>
          <Select
            flex={1}
            label="Departament"
            placeholder="Departament"
            searchable
            data={departments.map(dep => ({ label: dep.name, value: `${dep.id}` }))}
            value={departmentId}
            onChange={onDepartmentChange}
          />
          {departmentId != null ? (
            <EvaluationDepartmentSummary
              evaluationId={evaluationId}
              departmentId={Number(departmentId)}
            />
          ) : (
            <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de director departament sau decan" />
          )}
        </Stack>
      </Content>
    </Stack>
  );
}
